import {createFragmentContainer, graphql} from 'react-relay';
import {useIntl} from 'react-intl';

import {getFiltersAlphabetically} from '../../../forecast-app/shared/components/filters/filter_util';
import {DATE_PICKER_STYLE, ELEMENT_TYPE, FILTER_SECTION, FILTER_TYPE} from '../../../constants';
import {buildThirdLevelNavHeader} from '../../../forecast-app/shared/components/headers/header-bar/header_bar';
import Util from '../../../forecast-app/shared/util/util';
import {FILTER_SECTIONS} from '../../../forecast-app/shared/components/filters/FilterWrapper';
import {getSingleNumberLabel, PROJECT_PORTFOLIO_SINGLE_NUMBER} from './ProjectPortfolioSingleNumberOptions';
import tracking from '../../../tracking';
import {PROJECT_PORTFOLIO_REPORT_PAGES} from './ProjectPortfolioReport';
import {getPeriodEyeOptions} from './period/ProjectPortfolioReportPeriodEyeOptions';

const ProjectPortfolioReportHeaderBar = ({
	getBottomReportHeader,
	handleCsvDownload,
	selectedSingleValue,
	handleSetSingleValue,
	activePage,
	selectedDates,
	selectedYear,
	onChangeDates,
	onChangeYear,
	viewer: {
		id: viewerId,
		company: {modules},
		filters,
	},
}) => {
	const intl = useIntl();
	const hasFinance = Util.hasFinance(modules);
	const hasBaseline = Util.hasBaselineModule(modules);
	const hasLimitedFinancialAccess = Util.hasLimitedFinancialAccess();
	const hasRetainerPeriodRead = Util.AuthorizeViewerAccess('project-periods');
	const getHeaderLeftContent = () => {};

	const getHeaderRightContent = () => {
		const projectFilters = [
			FILTER_TYPE.PROJECT,
			FILTER_TYPE.PROJECT_STATUS,
			FILTER_TYPE.CONTACT,
			FILTER_TYPE.PROJECT_STAGE,
			FILTER_TYPE.CLIENT,
			FILTER_TYPE.LABEL,
			FILTER_TYPE.DEADLINE,
		];
		if (hasFinance) {
			projectFilters.push(FILTER_TYPE.PROJECT_TYPE, FILTER_TYPE.RATE_CARD);
		}
		const peopleFilters = [];
		const taskFilters = [];
		const filterProps = {
			defaultSection: FILTER_SECTIONS.PROJECTS,
			projectFilters: getFiltersAlphabetically(projectFilters, intl.formatMessage),
			peopleFilters,
			taskFilters,
			appliedFiltersName: `filters-project-portfolio-report`,
			filterSection: FILTER_SECTION.FINANCIAL_PORTFOLIO_REPORT,
			viewer: {id: viewerId, filters},
		};

		const additionalProps = {filterProps};

		if (activePage === PROJECT_PORTFOLIO_REPORT_PAGES.PERIOD.path) {
			additionalProps.eyeProps = {
				eyeOptions: getPeriodEyeOptions(hasFinance, hasBaseline, hasLimitedFinancialAccess, hasRetainerPeriodRead),
			};
		}

		const content = [];

		const filteredHeaderElements = getBottomReportHeader(additionalProps);

		content.push(...filteredHeaderElements);

		content.push({
			type: ELEMENT_TYPE.CSV,
			callback: handleCsvDownload,
		});

		if (hasFinance) {
			let singleValueOptions = [
				{
					value: PROJECT_PORTFOLIO_SINGLE_NUMBER.ACTUAL_REVENUE,
					label: getSingleNumberLabel(PROJECT_PORTFOLIO_SINGLE_NUMBER.ACTUAL_REVENUE, intl),
				},
				{
					value: PROJECT_PORTFOLIO_SINGLE_NUMBER.ACTUAL_COST,
					label: getSingleNumberLabel(PROJECT_PORTFOLIO_SINGLE_NUMBER.ACTUAL_COST, intl),
				},
				{
					value: PROJECT_PORTFOLIO_SINGLE_NUMBER.ACTUAL_PROFIT,
					label: getSingleNumberLabel(PROJECT_PORTFOLIO_SINGLE_NUMBER.ACTUAL_PROFIT, intl),
				},
				{
					value: PROJECT_PORTFOLIO_SINGLE_NUMBER.PLAN_REVENUE,
					label: getSingleNumberLabel(PROJECT_PORTFOLIO_SINGLE_NUMBER.PLAN_REVENUE, intl),
				},
				{
					value: PROJECT_PORTFOLIO_SINGLE_NUMBER.PLAN_COST,
					label: getSingleNumberLabel(PROJECT_PORTFOLIO_SINGLE_NUMBER.PLAN_COST, intl),
				},
				{
					value: PROJECT_PORTFOLIO_SINGLE_NUMBER.PLAN_PROFIT,
					label: getSingleNumberLabel(PROJECT_PORTFOLIO_SINGLE_NUMBER.PLAN_PROFIT, intl),
				},
				{
					value: PROJECT_PORTFOLIO_SINGLE_NUMBER.REMAINING_REVENUE,
					label: getSingleNumberLabel(PROJECT_PORTFOLIO_SINGLE_NUMBER.REMAINING_REVENUE, intl),
				},
				{
					value: PROJECT_PORTFOLIO_SINGLE_NUMBER.REMAINING_COST,
					label: getSingleNumberLabel(PROJECT_PORTFOLIO_SINGLE_NUMBER.REMAINING_COST, intl),
				},
				{
					value: PROJECT_PORTFOLIO_SINGLE_NUMBER.REMAINING_PROFIT,
					label: getSingleNumberLabel(PROJECT_PORTFOLIO_SINGLE_NUMBER.REMAINING_PROFIT, intl),
				},
				{
					value: PROJECT_PORTFOLIO_SINGLE_NUMBER.FORECAST_REVENUE,
					label: getSingleNumberLabel(PROJECT_PORTFOLIO_SINGLE_NUMBER.FORECAST_REVENUE, intl),
				},
				{
					value: PROJECT_PORTFOLIO_SINGLE_NUMBER.FORECAST_COST,
					label: getSingleNumberLabel(PROJECT_PORTFOLIO_SINGLE_NUMBER.FORECAST_COST, intl),
				},
				{
					value: PROJECT_PORTFOLIO_SINGLE_NUMBER.FORECAST_PROFIT,
					label: getSingleNumberLabel(PROJECT_PORTFOLIO_SINGLE_NUMBER.FORECAST_PROFIT, intl),
				},
				{
					value: PROJECT_PORTFOLIO_SINGLE_NUMBER.INVOICED,
					label: getSingleNumberLabel(PROJECT_PORTFOLIO_SINGLE_NUMBER.INVOICED, intl),
				},
				{
					value: PROJECT_PORTFOLIO_SINGLE_NUMBER.INVOICE_PAID,
					label: getSingleNumberLabel(PROJECT_PORTFOLIO_SINGLE_NUMBER.INVOICE_PAID, intl),
				},
			];

			if (hasLimitedFinancialAccess) {
				singleValueOptions = singleValueOptions.filter(item => {
					return !item.value.endsWith('Cost') && !item.value.endsWith('Profit');
				});
			}

			content.push({
				type: ELEMENT_TYPE.LEFT_DROPDOWN,
				dropdownOptions: singleValueOptions,
				value: selectedSingleValue,
				callback: newSelectedSingleValueType => {
					tracking.trackElementClicked('Single value type changed', {
						single_value_type: newSelectedSingleValueType.label,
					});
					handleSetSingleValue(newSelectedSingleValueType.value);
				},
				placeholder: '',
				userpilot: 'project-portfolio-single-value-dropdown',
			});
		}

		if (activePage === PROJECT_PORTFOLIO_REPORT_PAGES.PERIOD.path) {
			content.push({
				type: ELEMENT_TYPE.DATE_PICKER,
				isNewDateRange: true,
				startDate: selectedDates.startDate,
				endDate: selectedDates.endDate,
				handleDateRangeChange: onChangeDates,
				colorInherited: true,
				compactShowYear: true,
				datePickerStyle: DATE_PICKER_STYLE.SLIM,
				calendarOffsetX: 0,
				clearable: false,
				userpilot: 'project-portfolio-report-date-picker',
			});
		} else {
			content.push({
				type: ELEMENT_TYPE.YEAR_STEPPER,
				startYear: selectedYear,
				onYearChanged: onChangeYear,
			});
		}

		return content;
	};

	return buildThirdLevelNavHeader(getHeaderLeftContent(), getHeaderRightContent(), null, {noPadding: true}, false);
};

export default createFragmentContainer(ProjectPortfolioReportHeaderBar, {
	viewer: graphql`
		fragment ProjectPortfolioReportHeaderBar_viewer on Viewer {
			id
			availableFeatureFlags {
				key
			}
			company {
				tier
				modules {
					moduleType
				}
			}
			filters(first: 1000000, filterSection: FINANCIAL_PORTFOLIO_REPORT) @connection(key: "Viewer_filters", filters: []) {
				edges {
					node {
						id
						name
						section
						value
						updatedAt
					}
				}
			}
		}
	`,
});
